import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Form, FloatingLabel, Row, Col } from 'react-bootstrap';
import { ProvinceModel } from '../../../data/entities';
import AddressService from '../../../services/AddressService';
import { SectionHeader, Dropdown, Option, mapCountryToEnum } from '../../../components';
import { FormikErrors, FormikTouched } from 'formik';

export interface AddressFormValues {
  streetAddress: string;
  streetAddress2: string;
  city: string;
  country: string;
  provinceId: string;
  postalCode: string;
}

interface AddressFormSectionProps {
  values: AddressFormValues;
  errors: FormikErrors<AddressFormValues>;
  touched: FormikTouched<AddressFormValues>;
  handleChange: (e: React.ChangeEvent<any>) => void;
  handleBlur: (e: React.FocusEvent<any>) => void;
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
  defaultCountry?: number;
  defaultRegion?: number;
}

export function AddressFormSection({
  values,
  errors,
  touched,
  handleChange,
  handleBlur,
  setFieldValue,
  defaultCountry = 0, // Canada
  defaultRegion = 2,  // Alberta
}: AddressFormSectionProps) {
  const [allRegions, setAllRegions] = useState<ProvinceModel[] | null>(null);

  useEffect(() => {
    new AddressService()
      .getAllProvinces()
      .then(setAllRegions)
      .catch((error) => {
        console.error("Error fetching region data: ", error);
      });
  }, []);

  const countryList = useMemo(() => {
    if (!allRegions) return null;
    
    const countries: Option[] = [];
    let countryCount = 0;
    
    allRegions.forEach((province) => {
      if (!countries.some((option) => option.text.includes(province.country))) {
        countries.push({
          value: countryCount.toString(),
          text: province.country,
        });
        countryCount++;
      }
    });
    
    return countries;
  }, [allRegions]);

  const getRegionList = useCallback((selectedCountry: number): Option[] => {
    if (!allRegions) return [];
    
    return allRegions
      .filter((province) => mapCountryToEnum(province.country) === selectedCountry)
      .map((province) => ({
        value: province.id?.toString()!,
        text: province.name,
      }));
  }, [allRegions]);

  const regionList = getRegionList(parseInt(values.country));

  return (
    <div className="mb-3">
      <SectionHeader title="Address Information" />
      <div className="px-3 pt-3">
        {/* Street Address */}
        <Row className="mb-3">
          <Col lg={12}>
            <Form.Group controlId="streetAddress">
              <FloatingLabel label="Street Address">
                <Form.Control 
                  type="text"
                  name="streetAddress"
                  placeholder="Street Address"
                  value={values.streetAddress}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isInvalid={touched.streetAddress && !!errors.streetAddress}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.streetAddress}
                </Form.Control.Feedback>
              </FloatingLabel>
            </Form.Group>
          </Col>
        </Row>

        {/* Street Address 2 */}
        <Row className="mb-3">
          <Col lg={12}>
            <Form.Group controlId="streetAddress2">
              <FloatingLabel label="Apt / Unit (optional)">
                <Form.Control 
                  type="text"
                  name="streetAddress2"
                  placeholder="Apt / Unit"
                  value={values.streetAddress2}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
              </FloatingLabel>
            </Form.Group>
          </Col>
        </Row>

        {/* City */}
        <Row className="mb-3">
          <Col lg={12}>
            <Form.Group controlId="city">
              <FloatingLabel label="City">
                <Form.Control 
                  type="text"
                  name="city"
                  placeholder="City"
                  value={values.city}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isInvalid={touched.city && !!errors.city}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.city}
                </Form.Control.Feedback>
              </FloatingLabel>
            </Form.Group>
          </Col>
        </Row>

        {/* Country */}
        <Row className="mb-3">
          <Col lg={12}>
            <FloatingLabel
              controlId="country"
              label="Country"
              aria-label="Country"
              title="Country"
            >
              <Dropdown
                label="Country"
                selectedOption={parseInt(values.country)}
                id="country"
                options={countryList}
                disabled={false}
                onChangeSelected={(input) => {
                  setFieldValue("country", input);
                  setFieldValue("provinceId", "");
                }}
              />
            </FloatingLabel>
          </Col>
        </Row>

        {/* Region */}
        <Row className="mb-3">
          <Col lg={12}>
            <FloatingLabel
              controlId="region"
              label="Region"
              aria-label="Region"
              title="Region"
            >
              <Dropdown
                label="Region"
                selectedOption={values.provinceId ? parseInt(values.provinceId) : defaultRegion}
                id="region"
                options={regionList}
                disabled={false}
                onChangeSelected={(input) => {
                  setFieldValue("provinceId", input);
                }}
              />
            </FloatingLabel>
          </Col>
        </Row>

        {/* Postal Code */}
        <Row className="mb-0">
          <Col lg={12}>
            <Form.Group controlId="postalCode">
              <FloatingLabel label="Postal Code / Zip">
                <Form.Control 
                  type="text"
                  name="postalCode"
                  placeholder="Postal Code"
                  value={values.postalCode}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isInvalid={touched.postalCode && !!errors.postalCode}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.postalCode}
                </Form.Control.Feedback>
              </FloatingLabel>
            </Form.Group>
          </Col>
        </Row>
      </div>
    </div>
  );
}

export default AddressFormSection;